import { useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Modal, Pagination, Select, SelectItem, Tag, TextArea, TextInput } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { fromZoneInput, GAME_CATALOG, GAME_GROUP_LABEL, sameGameOf, toZoneInput, type GameGroup } from '@ruby-cms/shared';
import { CURRENCY, Money, When } from '../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';
import { useAuth } from '../auth/AuthContext';
import { SegmentSelect } from './Segments';

type Audience = { newDays?: number; vipMin?: number; deposited?: 'yes' | 'no'; userIds?: number[]; segmentId?: string };
type Code = {
  id: string; code: string; amount: string; maxUses: number; usedCount: number; status: string; expiresAt?: string | null; note?: string | null;
  batchId?: string | null; perPlayer: number; onePerDevice: boolean; onePerBank: boolean; audience?: Audience | null; wageringX?: number | null; createdAt: string;
  games?: string[] | null;
};
type Page = { items: Code[]; total: number; page: number; pageSize: number };

// Game scope (docs/specs/giftcode-pham-vi-game.md): the catalogue, one box per game (a game listed under two ids shows
// once and ticks both). "Sảnh ngoài" = catalogue group partner, "game nhà cái" = every other group.
const ALL_GAMES = GAME_CATALOG.map((g) => g.id);
const HOUSE_GAMES = GAME_CATALOG.filter((g) => g.group !== 'partner').map((g) => g.id);
const PARTNER_GAMES = GAME_CATALOG.filter((g) => g.group === 'partner').map((g) => g.id);
const SCOPE_BOXES = GAME_CATALOG.filter((g) => sameGameOf(g.id)[0] === g.id);
const isAll = (games: string[]) => ALL_GAMES.every((id) => games.includes(id));

// the rules form, shared by create, batch and edit
type RuleForm = { perPlayer: string; onePerDevice: boolean; onePerBank: boolean; newDays: string; vipMin: string; deposited: string; userIds: string; wageringX: string; segmentId: string; games: string[] };
const noRules: RuleForm = { perPlayer: '1', onePerDevice: false, onePerBank: false, newDays: '', vipMin: '', deposited: '', userIds: '', wageringX: '', segmentId: '', games: ALL_GAMES };
const rulesOf = (c: Code): RuleForm => ({
  perPlayer: String(c.perPlayer), onePerDevice: c.onePerDevice, onePerBank: c.onePerBank,
  newDays: c.audience?.newDays ? String(c.audience.newDays) : '', vipMin: c.audience?.vipMin ? String(c.audience.vipMin) : '',
  deposited: c.audience?.deposited ?? '', userIds: (c.audience?.userIds ?? []).join(', '), wageringX: c.wageringX == null ? '' : String(c.wageringX),
  segmentId: c.audience?.segmentId ?? '', games: c.games ?? ALL_GAMES,
});
function rulesBody(r: RuleForm) {
  const audience: Audience = {};
  if (r.newDays) audience.newDays = Number(r.newDays);
  if (r.vipMin) audience.vipMin = Number(r.vipMin);
  if (r.deposited === 'yes' || r.deposited === 'no') audience.deposited = r.deposited;
  const ids = r.userIds.split(/[\s,;]+/).filter(Boolean).map(Number).filter((n) => Number.isInteger(n) && n > 0);
  if (ids.length) audience.userIds = ids;
  if (r.segmentId) audience.segmentId = r.segmentId;
  return {
    perPlayer: Math.max(1, Number(r.perPlayer) || 1), onePerDevice: r.onePerDevice, onePerBank: r.onePerBank,
    audience: Object.keys(audience).length ? audience : null, wageringX: r.wageringX === '' ? null : Number(r.wageringX),
    // every game ticked = null (every game, also games added later)
    games: isAll(r.games) ? null : [...r.games].sort(),
  };
}
const localIso = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : toZoneInput(d); // the time of the CMS time zone
};
const isoOf = (local: string) => (local ? fromZoneInput(local)?.toISOString() ?? null : null);

function RuleFields({ id, r, set, batch }: { id: string; r: RuleForm; set: (r: RuleForm) => void; batch: boolean }) {
  const digits = (k: keyof RuleForm) => (e: { target: { value: string } }) => set({ ...r, [k]: e.target.value.replace(/\D/g, '') });
  const { can } = useAuth();
  return (
    <>
      <p className="cds--label">Giới hạn{batch ? ' (tính chung cả lô)' : ''}</p>
      {batch ? <TextInput id={id + '-pp'} labelText="Mỗi người chơi nhận tối đa (mã trong lô)" value={r.perPlayer} onChange={digits('perPlayer')} /> : null}
      <Checkbox id={id + '-dv'} labelText="Mỗi thiết bị chỉ nhận một lần" checked={r.onePerDevice} onChange={(_e, { checked }) => set({ ...r, onePerDevice: checked })} />
      <Checkbox id={id + '-bk'} labelText="Mỗi tài khoản ngân hàng chỉ nhận một lần (bắt buộc đã liên kết ngân hàng)" checked={r.onePerBank} onChange={(_e, { checked }) => set({ ...r, onePerBank: checked })} />
      <p className="cds--label">Đối tượng (để trống = mọi người chơi)</p>
      {can('crm.manage') || r.segmentId ? <SegmentSelect id={id + '-seg'} value={r.segmentId} onChange={(v) => set({ ...r, segmentId: v })}
        helperText="Chỉ người đang thuộc phân khúc lúc nhập mã mới nhận được." /> : null}
      <TextInput id={id + '-nd'} labelText="Chỉ tài khoản mới đăng ký trong (ngày)" value={r.newDays} onChange={digits('newDays')} />
      <TextInput id={id + '-vip'} labelText="Điểm VIP tối thiểu" value={r.vipMin} onChange={digits('vipMin')} />
      <Select id={id + '-dep'} labelText="Đã nạp tiền" value={r.deposited} onChange={(e) => set({ ...r, deposited: e.target.value })}>
        <SelectItem value="" text="Không xét" />
        <SelectItem value="yes" text="Chỉ người đã nạp" />
        <SelectItem value="no" text="Chỉ người chưa nạp" />
      </Select>
      <TextArea id={id + '-ids'} labelText="Chỉ những người chơi này (mã người chơi, cách nhau dấu phẩy)" rows={2} value={r.userIds} onChange={(e) => set({ ...r, userIds: e.target.value })} />
      <TextInput id={id + '-wx'} labelText="Hệ số vòng cược" helperText="Tiền thưởng x hệ số phải cược trước khi rút. Để trống = theo cài đặt vòng cược giftcode."
        value={r.wageringX} onChange={digits('wageringX')} />
      <GameScope id={id + '-gm'} games={r.games} set={(games) => set({ ...r, games })} />
    </>
  );
}

// Games the code's wagering counts on. While the player owes it, bets in unticked games are refused.
function GameScope({ id, games, set }: { id: string; games: string[]; set: (g: string[]) => void }) {
  const on = new Set(games);
  const toggle = (gid: string, checked: boolean) => {
    const ids = sameGameOf(gid);
    set(checked ? [...new Set([...games, ...ids])] : games.filter((x) => !ids.includes(x)));
  };
  const groups = Object.keys(GAME_GROUP_LABEL) as GameGroup[];
  return (
    <fieldset className="gc-games" id={id}>
      <p className="cds--label">Game áp dụng ({isAll(games) ? 'tất cả' : SCOPE_BOXES.filter((g) => on.has(g.id)).length + ' / ' + SCOPE_BOXES.length + ' game'})</p>
      <p className="muted">Vòng cược của mã chỉ tính ở game được chọn. Khi người chơi còn vòng cược của mã, cược ở game không chọn bị từ chối.</p>
      <div className="gc-games__quick">
        <Button size="sm" kind="tertiary" onClick={() => set(ALL_GAMES)}>Tất cả</Button>
        <Button size="sm" kind="tertiary" onClick={() => set(HOUSE_GAMES)}>Tất cả game nhà cái</Button>
        <Button size="sm" kind="tertiary" onClick={() => set(PARTNER_GAMES)}>Tất cả sảnh ngoài</Button>
        <Button size="sm" kind="ghost" onClick={() => set([])}>Bỏ chọn hết</Button>
      </div>
      {groups.map((grp) => {
        const list = SCOPE_BOXES.filter((g) => g.group === grp);
        if (!list.length) return null;
        return (
          <div key={grp} className="gc-games__group">
            <p className="cds--label">{GAME_GROUP_LABEL[grp]}{grp === 'partner' ? ' (sảnh ngoài)' : ''}</p>
            <div className="gc-games__list">
              {list.map((g) => (
                <Checkbox key={g.id} id={id + '-' + g.id} labelText={g.name} checked={on.has(g.id)}
                  onChange={(_e, { checked }) => toggle(g.id, checked)} />
              ))}
            </div>
          </div>
        );
      })}
      {!games.length ? <p className="cds--form-requirement" style={{ display: 'block' }}>Chọn ít nhất một game áp dụng.</p> : null}
    </fieldset>
  );
}

// "Tất cả" or the names of the ticked games (one name per game listed under two ids)
function gamesText(games?: string[] | null) {
  if (!games) return 'Tất cả';
  const names = SCOPE_BOXES.filter((g) => games.includes(g.id)).map((g) => g.name);
  return names.length <= 3 ? names.join(', ') : names.slice(0, 3).join(', ') + ' +' + (names.length - 3);
}

function ruleText(c: Code) {
  const out: string[] = [];
  if (c.batchId && c.perPlayer > 1) out.push(`${c.perPlayer} mã/người`);
  if (c.onePerDevice) out.push('1/thiết bị');
  if (c.onePerBank) out.push('1/ngân hàng');
  const a = c.audience;
  if (a?.newDays) out.push(`mới ≤ ${a.newDays} ngày`);
  if (a?.vipMin) out.push(`VIP ≥ ${a.vipMin.toLocaleString('vi-VN')}`);
  if (a?.deposited) out.push(a.deposited === 'yes' ? 'đã nạp' : 'chưa nạp');
  if (a?.userIds?.length) out.push(`${a.userIds.length} người chỉ định`);
  if (a?.segmentId) out.push('theo phân khúc');
  if (c.wageringX != null) out.push(`cược x${c.wageringX}`);
  return out.join(' · ');
}

const FIELDS: FilterField[] = [
  { key: 'code', label: 'Mã', kind: 'text' },
  { key: 'status', label: 'Trạng thái', kind: 'select', options: [['', 'Tất cả'], ['ACTIVE', 'Bật'], ['DISABLED', 'Tắt']] },
  { key: 'batchId', label: 'Lô (mã lô)', kind: 'text' },
];
const KEYS = FIELDS.map((f) => f.key);

export function GiftcodesPage() {
  const qc = useQueryClient();
  const f = useUrlFilters(KEYS);
  const q = useQuery({
    queryKey: ['giftcodes', f.query, f.page, f.pageSize],
    queryFn: () => api.get<Page>('/api/admin/giftcodes', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });
  const done = () => qc.invalidateQueries({ queryKey: ['giftcodes'] });

  // create one code / a batch
  const [mode, setMode] = useState<'one' | 'batch' | null>(null);
  const [code, setCode] = useState('');
  const [prefix, setPrefix] = useState('');
  const [count, setCount] = useState('100');
  const [amount, setAmount] = useState('');
  const [maxUses, setMaxUses] = useState('1');
  const [expires, setExpires] = useState('');
  const [note, setNote] = useState('');
  const [rules, setRules] = useState<RuleForm>(noRules);
  const reset = () => { setMode(null); setCode(''); setPrefix(''); setAmount(''); setMaxUses('1'); setExpires(''); setNote(''); setRules(noRules); };
  const create = useMutation({
    mutationFn: () => {
      const common = { amount, maxUses: Number(maxUses) || 1, ...(expires ? { expiresAt: isoOf(expires) } : {}), ...(note ? { note } : {}), ...rulesBody(rules) };
      return mode === 'batch'
        ? api.post<{ batchId: string }>('/api/admin/giftcodes/batch', { ...common, prefix, count: Number(count) })
        : api.post('/api/admin/giftcodes', { ...common, code, perPlayer: 1 });
    },
    onSuccess: (r) => {
      const batchId = (r as { batchId?: string }).batchId;
      reset();
      if (batchId) f.set({ code: '', status: '', batchId });
      done();
    },
  });

  // edit one code, or its whole batch
  const [pick, setPick] = useState<Code | null>(null);
  const [eStatus, setEStatus] = useState('ACTIVE');
  const [eMax, setEMax] = useState('');
  const [eExp, setEExp] = useState('');
  const [eNote, setENote] = useState('');
  const [eRules, setERules] = useState<RuleForm>(noRules);
  const [wholeBatch, setWholeBatch] = useState(false);
  const open = (c: Code) => {
    setPick(c); setEStatus(c.status); setEMax(String(c.maxUses)); setEExp(localIso(c.expiresAt)); setENote(c.note ?? ''); setERules(rulesOf(c)); setWholeBatch(false);
  };
  const edit = useMutation({
    mutationFn: () => {
      // only what the admin changed: the audit row then shows exactly that change
      const was = { status: pick!.status, maxUses: pick!.maxUses, expiresAt: localIso(pick!.expiresAt), note: pick!.note ?? '', ...rulesBody(rulesOf(pick!)) };
      const now = { status: eStatus, maxUses: Number(eMax), expiresAt: eExp, note: eNote, ...rulesBody(eRules) };
      const body: Record<string, unknown> = {};
      for (const k of Object.keys(now) as (keyof typeof now)[]) {
        if (JSON.stringify(now[k]) === JSON.stringify(was[k])) continue;
        body[k] = k === 'expiresAt' ? isoOf(eExp) : k === 'note' ? eNote || null : now[k];
      }
      if (!pick!.batchId) delete body.perPlayer;
      return wholeBatch && pick!.batchId
        ? api.patch('/api/admin/giftcodes/batches/' + pick!.batchId, body)
        : api.patch('/api/admin/giftcodes/' + pick!.id, body);
    },
    onSuccess: () => { setPick(null); done(); },
  });

  const columns: ColumnDef<Code, any>[] = [
    { header: 'Mã', accessorKey: 'code', cell: (c) => <span className="num">{c.getValue()}</span> },
    { header: 'Số tiền', accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} /> },
    { header: 'Đã dùng', id: 'u', cell: (c) => c.row.original.usedCount + ' / ' + c.row.original.maxUses },
    { header: 'Hết hạn', accessorKey: 'expiresAt', cell: (c) => c.getValue() ? <When v={c.getValue()} /> : 'Không' },
    { header: 'Điều kiện', id: 'rules', cell: (c) => ruleText(c.row.original) || '—' },
    { header: 'Game áp dụng', id: 'games', cell: (c) => <span title={c.row.original.games ? c.row.original.games.join(', ') : ''}>{gamesText(c.row.original.games)}</span> },
    { header: 'Lô', accessorKey: 'batchId', cell: (c) => c.getValue()
      ? <button type="button" className="cell-link" onClick={(e) => { e.stopPropagation(); f.set({ ...f.values, batchId: c.getValue() }); }}>{String(c.getValue()).slice(0, 8)}</button>
      : '—' },
    { header: 'Trạng thái', accessorKey: 'status', cell: (c) => <Tag size="sm" type={c.getValue() === 'ACTIVE' ? 'green' : 'gray'}>{c.getValue() === 'ACTIVE' ? 'Bật' : 'Tắt'}</Tag> },
  ];

  return (
    <div className="page">
      <PageHeader title="Giftcode" lead="Mã cộng tiền trên hệ A. Kiểm tra đối tượng, thiết bị, ngân hàng, giữ lượt và tạo lệnh cộng tiền trong cùng một bước: không mất, không cộng hai lần."
        actions={<>
          <Button size="sm" kind="tertiary" onClick={() => setMode('batch')}>Tạo lô mã</Button>
          <Button size="sm" onClick={() => setMode('one')}>Tạo mã</Button>
        </>} />
      <FilterBar id="gc" fields={FIELDS} filters={f} exportPath="/api/admin/giftcodes">
        <Link className="cell-link" to="/giftcodes/redemptions">Lượt đã nhập</Link>
      </FilterBar>
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error} onRetry={() => q.refetch()}
        onRowClick={open} rowKey={(r) => r.id}
        empty={{ title: f.active ? 'Không có mã khớp bộ lọc' : 'Chưa có mã', body: f.active ? 'Nới bộ lọc để xem thêm.' : 'Tạo một mã hoặc một lô mã rồi đưa cho người chơi nhập trong game.' }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[25, 50, 100, 200]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} mã`}
          onChange={({ page, pageSize }) => f.setPage(page, pageSize)} />
      ) : null}

      <Modal open={mode !== null} modalHeading={mode === 'batch' ? 'Tạo lô giftcode' : 'Tạo giftcode'} primaryButtonText="Tạo" secondaryButtonText="Hủy"
        primaryButtonDisabled={!amount || (mode === 'one' ? !code : !(Number(count) > 0)) || !rules.games.length || create.isPending}
        onRequestClose={reset} onRequestSubmit={() => create.mutate()}>
        <div className="stack">
          {mode === 'batch' ? (
            <>
              <TextInput id="gc-pre" labelText="Tiền tố (tùy chọn)" helperText="Phần còn lại là ký tự ngẫu nhiên" value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} />
              <TextInput id="gc-count" labelText="Số mã (tối đa 5.000)" value={count} onChange={(e) => setCount(e.target.value.replace(/\D/g, ''))} />
            </>
          ) : <TextInput id="gc-code" labelText="Mã" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />}
          <TextInput id="gc-amt" labelText={`Số tiền mỗi lần nhập (${CURRENCY.symbol})`} value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))} />
          <TextInput id="gc-max" labelText={mode === 'batch' ? 'Số lần dùng tối đa của mỗi mã' : 'Số lần dùng tối đa'} value={maxUses} onChange={(e) => setMaxUses(e.target.value.replace(/\D/g, ''))} />
          <TextInput id="gc-exp" type="datetime-local" labelText="Hết hạn (để trống = không hết hạn)" value={expires} onChange={(e) => setExpires(e.target.value)} />
          <TextInput id="gc-note" labelText="Ghi chú" value={note} onChange={(e) => setNote(e.target.value)} />
          <RuleFields id="gc-new" r={rules} set={setRules} batch={mode === 'batch'} />
          <InlineError error={create.error} />
        </div>
      </Modal>

      <Modal open={!!pick} modalHeading={pick ? 'Sửa mã ' + pick.code : ''} primaryButtonText="Lưu" secondaryButtonText="Đóng"
        primaryButtonDisabled={!(Number(eMax) > 0) || !eRules.games.length || edit.isPending} onRequestClose={() => setPick(null)} onRequestSubmit={() => edit.mutate()}>
        {pick ? (
          <div className="stack">
            <p>Đã dùng {pick.usedCount} / {pick.maxUses} · <Money v={pick.amount} /> mỗi lần.</p>
            {pick.batchId ? <Checkbox id="gc-whole" labelText="Áp dụng cho cả lô mã này" checked={wholeBatch} onChange={(_e, { checked }) => setWholeBatch(checked)} /> : null}
            <Select id="gc-st" labelText="Trạng thái" value={eStatus} onChange={(e) => setEStatus(e.target.value)}>
              <SelectItem value="ACTIVE" text="Bật" />
              <SelectItem value="DISABLED" text="Tắt (không ai nhập được)" />
            </Select>
            <TextInput id="gc-emax" labelText="Số lần dùng tối đa" value={eMax} onChange={(e) => setEMax(e.target.value.replace(/\D/g, ''))} />
            <TextInput id="gc-eexp" type="datetime-local" labelText="Hết hạn (để trống = không hết hạn)" value={eExp} onChange={(e) => setEExp(e.target.value)} />
            <TextInput id="gc-enote" labelText="Ghi chú" value={eNote} onChange={(e) => setENote(e.target.value)} />
            <RuleFields id="gc-edit" r={eRules} set={setERules} batch={!!pick.batchId} />
            <InlineError error={edit.error} />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
